import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import FormRow from '../../common/input/FormRow';
import TextWinds from '../../common/TextWinds';
import {
  birthdayRules,
  COUNTRY_CODE_HELP,
  COUNTRY_CODE_REQUIRED_MESSAGE,
  countryCodeRules,
  DEFAULT_COUNTRY_CODE,
  nameRules,
  phoneRules,
  toCountryCodeRequest,
} from '../../../constants/validation';
import { putStudentGuardianInfo } from '../../../api/utils/student/profile/apis';
import {
  EMPTY_GUARDIAN,
  StudentGuardianInfoForm,
  isGuardianChanged,
  normalizeGuardian,
  toGuardianInfoForm,
} from '../../../api/utils/student/profile/mapper';
import { StudentProfile } from '../../../constants/types/student/profile/type';
import { useSectionSave } from '../../../hooks/student/profile/useSectionSave';
import { Field } from '../../common/form/Field';
import SectionHeader from '../../common/form/SectionHeader';
import AccordionPanel, {
  AccordionControlProps,
} from '../../common/form/AccordionPanel';
import { TermAgreement, TermAgreementItem } from '../../common/TermAgreement';
import { TermType } from '../../common/Term';
import ErrorInput from '../../common/ErrorInput';
import HFbutton from '../../button/HFbutton';

/** 새 보호자 등록에 필요한 필수 약관. 최초 로그인의 보호자 등록과 같다. */
const GUARDIAN_AGREEMENTS: TermAgreementItem[] = [
  { termType: TermType.PRIVACY_POLICY, essential: true },
  { termType: TermType.PRIVACY_CONSENT, essential: true },
];

/**
 * 학생 페이지 > 내 정보 > 보호자 정보 영역
 * @description
 * - 학생이 자신의 보호자(guardianInfo)를 직접 등록/수정합니다.
 * - 조회 응답도 저장 요청도 보호자(guardianInfo)는 객체 1개라 한 명만 다룹니다.
 * - 폼과 저장은 이 영역이 직접 처리하고, 저장 후 재조회만 부모 컴포넌트에 맡깁니다.
 * - LMS > 학생 정보 수정 화면과 달리 국가 코드를 함께 입력받고,
 *   새 보호자를 등록할 때는 개인정보 수집/이용 동의를 받습니다.
 */
const GuardianInfoSection = ({
  profile,
  section,
  onSaved,
}: {
  /** 조회한 내 정보. 폼의 원본 값이자 취소했을 때 되돌릴 값이다. */
  profile: StudentProfile;
  section: AccordionControlProps;
  /** 저장 성공 후 재조회. */
  onSaved: () => void;
}) => {
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<StudentGuardianInfoForm>({
    defaultValues: toGuardianInfoForm(profile),
  });

  const isEditing = section.isEditing;
  /** 입력 중인 보호자. 등록/삭제로 통째로 바뀌므로 폼 값을 구독해서 본다. */
  const guardian = useWatch({ control, name: 'guardianInfo' });
  const guardianErrors = errors.guardianInfo;

  /**
   * 새 보호자를 등록하는 중인지.
   * 등록된 보호자가 없던 상태에서 보호자를 넣었을 때만 개인정보 동의가 필요하다.
   * (이미 등록된 보호자를 고치는 것은 등록 때 받은 동의가 유지된다)
   */
  const isAddingGuardian =
    !!guardian && !toGuardianInfoForm(profile).guardianInfo;

  // 재조회로 값이 바뀌면 폼도 그 값으로 맞춘다.
  useEffect(() => {
    reset(toGuardianInfoForm(profile));
  }, [profile, reset]);

  const { isSaving, submit } = useSectionSave({
    onClose: section.onClose,
    onSaved,
  });

  /** 입력을 조회 시점으로 되돌리고 수정을 끝낸다. */
  const cancel = () => {
    reset(toGuardianInfoForm(profile));
    section.onClose();
  };

  const onSubmit = (form: StudentGuardianInfoForm) =>
    submit(async () => {
      const origin = toGuardianInfoForm(profile).guardianInfo;
      if (!isGuardianChanged(form.guardianInfo, origin)) return false;

      const guardianInfo = normalizeGuardian(form.guardianInfo);

      // 보호자를 삭제하고 저장하면 { guardianInfo: null }을 보낸다.
      if (!guardianInfo) {
        await putStudentGuardianInfo(null);
        return true;
      }

      // 보호자 등록/수정
      await putStudentGuardianInfo({
        name: guardianInfo.name,
        phone: guardianInfo.phone,
        gender: guardianInfo.gender as 'MALE' | 'FEMALE',
        birthday: guardianInfo.birthday ?? '',
        // 국가 코드는 폼에서 숫자만 다루므로 보낼 때 '+'를 붙인다.
        countryCode: toCountryCodeRequest(guardianInfo.countryCode),
        agreementPersonalInfo: true,
      });
      return true;
    });

  /**
   * 저장 전 보호자 이름의 앞뒤 공백을 제거한다.
   * 화면 값 자체를 바꾸므로 이후의 검증/변경 비교도 공백이 제거된 값으로 수행된다.
   * 나머지 항목은 직접 입력하는 값이 아니거나(생년월일·성별),
   * 저장 형태로 맞출 때 숫자만 남는다. (연락처·국가 코드)
   */
  const save = () => {
    const name = getValues('guardianInfo.name');
    if (typeof name === 'string' && name !== name.trim()) {
      setValue('guardianInfo.name', name.trim());
    }
    // 검증에 걸리면 handleSubmit이 첫 오류 항목으로 이동시킨다.
    handleSubmit(onSubmit)();
  };

  return (
    <section>
      <SectionHeader
        title="보호자 정보"
        isEditing={isEditing}
        disabled={section.disabled}
        isSaving={isSaving}
        onEdit={section.onEdit}
        onCancel={cancel}
        onSave={save}
        isOpen={section.isOpen}
        onToggle={section.onToggle}
      />
      <AccordionPanel isOpen={section.isOpen}>
        <div className="mt-5 flex flex-col gap-y-2.5">
          {!guardian ? (
            <TextWinds type="content_body2" className="m-auto mb-4 text-gray6">
              등록된 보호자가 없습니다
            </TextWinds>
          ) : (
            <div className="flex flex-col gap-y-4 rounded-lg bg-gray0 p-4">
              <div className="flex justify-end">
                {isEditing && (
                  <HFbutton
                    type="red-dashed"
                    width="auto"
                    height={36}
                    onClick={() => setValue('guardianInfo', null)}
                  >
                    <span className="flex items-center justify-center gap-2 text-sm">
                      <TrashIcon className="size-5" />
                      삭제하기
                    </span>
                  </HFbutton>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <Field
                  label="보호자 이름"
                  error={guardianErrors?.name?.message}
                >
                  <FormRow
                    type="text"
                    name="guardianInfo.name"
                    control={control}
                    placeholder="보호자 이름 입력"
                    rules={nameRules({
                      label: '보호자 이름',
                      required: true,
                    })}
                    error={!!guardianErrors?.name}
                    disabled={!isEditing}
                    autoComplete="off"
                  />
                </Field>

                <Field
                  label="보호자 전화번호"
                  // 국가 코드와 번호를 한 줄로 묶어서 보여주므로 오류도 한 자리에 낸다.
                  error={
                    guardianErrors?.countryCode?.message ??
                    guardianErrors?.phone?.message
                  }
                  // 입력 안내이므로 수정 중일 때만 띄운다.
                  help={isEditing ? COUNTRY_CODE_HELP : undefined}
                >
                  <div className="flex gap-2">
                    <div className="w-[92px] shrink-0">
                      <FormRow
                        type="country-code"
                        name="guardianInfo.countryCode"
                        control={control}
                        placeholder={DEFAULT_COUNTRY_CODE}
                        rules={countryCodeRules({
                          required: COUNTRY_CODE_REQUIRED_MESSAGE,
                          // 국가 코드를 바꾸면 전화번호도 새 기준으로 다시 검사한다.
                          deps: ['guardianInfo.phone'],
                        })}
                        error={!!guardianErrors?.countryCode}
                        disabled={!isEditing}
                        autoComplete="off"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <FormRow
                        type="phone"
                        name="guardianInfo.phone"
                        control={control}
                        placeholder="- 없이 번호만 입력"
                        rules={phoneRules({
                          label: '보호자 전화번호',
                          required: true,
                          countryCode: guardian.countryCode,
                        })}
                        error={!!guardianErrors?.phone}
                        disabled={!isEditing}
                        autoComplete="off"
                        countryCode={guardian.countryCode}
                      />
                    </div>
                  </div>
                </Field>

                <Field
                  label="보호자 생년월일"
                  error={guardianErrors?.birthday?.message}
                >
                  <FormRow
                    type="birthday"
                    name="guardianInfo.birthday"
                    control={control}
                    rules={birthdayRules({
                      label: '보호자 생년월일',
                      required: true,
                    })}
                    disabled={!isEditing}
                    error={!!guardianErrors?.birthday}
                  />
                </Field>

                <Field
                  label="보호자 성별"
                  error={guardianErrors?.gender?.message}
                >
                  <FormRow
                    type="gender"
                    name="guardianInfo.gender"
                    control={control}
                    rules={{
                      required: '보호자 성별을 선택해주세요',
                    }}
                    error={!!guardianErrors?.gender}
                    disabled={!isEditing}
                  />
                </Field>
              </div>

              {/* 보호자를 추가할때만 약관 동의를 받음. 동의해야만 보호자 정보 등록 가능하므로 수정할때 굳이 노출하지 않는다. */}
              {isAddingGuardian && (
                <Controller
                  name="guardianAgreed"
                  control={control}
                  rules={{ required: '약관에 동의해주세요.' }}
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-2">
                      <TermAgreement
                        agreementList={GUARDIAN_AGREEMENTS}
                        essentialChecked={field.onChange}
                        error={!!fieldState.error}
                      />
                      {fieldState.error && (
                        <ErrorInput text={fieldState.error.message} />
                      )}
                    </div>
                  )}
                />
              )}
            </div>
          )}

          {isEditing && !guardian && (
            <HFbutton
              type="primary-dashed"
              onClick={() => setValue('guardianInfo', EMPTY_GUARDIAN)}
            >
              <span className="flex items-center justify-center gap-2 text-sm">
                <PlusCircleIcon className="size-5" />
                보호자 추가하기
              </span>
            </HFbutton>
          )}
        </div>
      </AccordionPanel>
    </section>
  );
};

export default GuardianInfoSection;
